import { mergeAssetsPayload, validateAssetsPayload, } from './asset-files.js';
import { COMPONENT_UI_CONFIG_FIELDS } from './components/index.js';
import { fieldDefaultPayload, mergeFieldPayload, validateFieldPayload, } from './fields.js';
import { UI_CONFIG_REPORT_FINGERPRINT_LENGTH } from './fingerprint.js';
import { mergeNavAliasesPayload, NAV_TABS_FIELDS, NAV_TABS_PREFIX, validateNavAliasesPayload, } from './nav.js';
import { mergePagesPayload, PAGES_DEFAULT_PAYLOAD, validatePagesPayload, } from './pages.js';
import { EXTRA_SCREEN_UI_CONFIG_FIELDS, } from './screen-fields/index.js';
import { MEDIA_DEFAULT_PAYLOAD, mergeMediaPayload, validateMediaPayload, } from './media.js';
import { mergeScreenPayload, screenDefaultPayload, screenFileDefinitions, validateScreenPayload, } from './screens.js';
import { mergeTextsPayload, validateTextsPayload, } from './texts.js';
import { mergeTokensPayload, TOKENS_DEFAULT_PAYLOAD, validateTokensPayload, } from './tokens.js';
export const UI_CONFIG_APPLY_POLICIES = [
    'NEXT_LAUNCH',
    'SAFE_POINT',
    'URGENT',
    'IMMEDIATE',
];
export const isUiConfigApplyPolicy = (value) => typeof value === 'string' && UI_CONFIG_APPLY_POLICIES.includes(value);
function defineFile(file) {
    return {
        ...file,
        mergeOverBuiltIn: (builtIn, received) => file.merge(file.validate(builtIn).payload ?? file.defaultPayload, received),
    };
}
function screenFile(screen) {
    return defineFile({
        name: `screen.${screen}`,
        kind: 'fields',
        fields: { prefix: screen, definitions: screenFileDefinitions(screen) },
        schemaVersion: screen === 'broadcast' ? 4 : screen === 'common' ? 2 : 1,
        applyPolicy: 'NEXT_LAUNCH',
        defaultPayload: screenDefaultPayload(screen),
        validate: (payload) => validateScreenPayload(screen, payload),
        merge: (builtIn, received) => mergeScreenPayload(screen, builtIn, received),
    });
}
function fieldFile(name, prefix, definitions) {
    return defineFile({
        name,
        kind: 'fields',
        fields: { prefix, definitions },
        schemaVersion: 1,
        applyPolicy: 'NEXT_LAUNCH',
        defaultPayload: fieldDefaultPayload(prefix, definitions),
        validate: (payload) => validateFieldPayload(name, prefix, definitions, payload),
        merge: (builtIn, received) => mergeFieldPayload(prefix, definitions, builtIn, received),
    });
}
const componentFile = (name) => fieldFile(`component.${name}`, name, COMPONENT_UI_CONFIG_FIELDS[name]);
const extraScreenFile = (name) => fieldFile(`screen.${name}`, name, EXTRA_SCREEN_UI_CONFIG_FIELDS[name]);
function textFile(language) {
    return defineFile({
        name: `text.${language}`,
        kind: 'texts',
        schemaVersion: 1,
        applyPolicy: 'SAFE_POINT',
        defaultPayload: {},
        validate: (payload) => validateTextsPayload(language, payload),
        merge: mergeTextsPayload,
    });
}
function assetFile(kind) {
    return defineFile({
        name: `asset.${kind}`,
        kind: 'assets',
        schemaVersion: 1,
        applyPolicy: 'NEXT_LAUNCH',
        defaultPayload: {},
        validate: (payload) => validateAssetsPayload(kind, payload),
        merge: (builtIn, received) => mergeAssetsPayload(kind, builtIn, received),
    });
}
export const UI_CONFIG_FILES = {
    tokens: defineFile({
        name: 'tokens',
        kind: 'tokens',
        schemaVersion: 4,
        applyPolicy: 'NEXT_LAUNCH',
        defaultPayload: TOKENS_DEFAULT_PAYLOAD,
        validate: validateTokensPayload,
        merge: mergeTokensPayload,
    }),
    'component.media': defineFile({
        name: 'component.media',
        kind: 'media',
        schemaVersion: 1,
        applyPolicy: 'NEXT_LAUNCH',
        defaultPayload: MEDIA_DEFAULT_PAYLOAD,
        validate: validateMediaPayload,
        merge: mergeMediaPayload,
    }),
    'component.layout': componentFile('layout'),
    'component.button': componentFile('button'),
    'component.badge': componentFile('badge'),
    'component.input': componentFile('input'),
    'component.chip': componentFile('chip'),
    'component.card': componentFile('card'),
    'component.listItem': componentFile('listItem'),
    'component.avatar': componentFile('avatar'),
    'component.appBar': componentFile('appBar'),
    'component.tabBar': componentFile('tabBar'),
    'component.sheet': componentFile('sheet'),
    'component.dialog': componentFile('dialog'),
    'component.toast': componentFile('toast'),
    'component.emptyState': componentFile('emptyState'),
    'component.loading': componentFile('loading'),
    'component.carousel': componentFile('carousel'),
    'component.text': componentFile('text'),
    'component.order': componentFile('order'),
    'screen.common': screenFile('common'),
    'screen.home': screenFile('home'),
    'screen.market': screenFile('market'),
    'screen.lounge': screenFile('lounge'),
    'screen.chat': screenFile('chat'),
    'screen.me': screenFile('me'),
    'screen.broadcast': screenFile('broadcast'),
    'screen.settings': extraScreenFile('settings'),
    'screen.search': extraScreenFile('search'),
    'screen.profile': extraScreenFile('profile'),
    'screen.product': extraScreenFile('product'),
    'screen.scheduledBroadcast': extraScreenFile('scheduledBroadcast'),
    'nav.tabs': fieldFile('nav.tabs', NAV_TABS_PREFIX, NAV_TABS_FIELDS),
    'nav.aliases': defineFile({
        name: 'nav.aliases',
        kind: 'aliases',
        schemaVersion: 1,
        applyPolicy: 'NEXT_LAUNCH',
        defaultPayload: {},
        validate: validateNavAliasesPayload,
        merge: mergeNavAliasesPayload,
    }),
    'text.ko': textFile('ko'),
    'text.en': textFile('en'),
    'text.vi': textFile('vi'),
    'asset.icons': assetFile('icons'),
    'asset.images': assetFile('images'),
    'page.index': defineFile({
        name: 'page.index',
        kind: 'pages',
        appDelivered: false,
        schemaVersion: 1,
        applyPolicy: 'IMMEDIATE',
        defaultPayload: PAGES_DEFAULT_PAYLOAD,
        validate: validatePagesPayload,
        merge: mergePagesPayload,
    }),
};
export const UI_CONFIG_FILE_NAMES = [
    'tokens',
    'component.layout',
    'component.button',
    'component.badge',
    'component.input',
    'component.chip',
    'component.card',
    'component.listItem',
    'component.avatar',
    'component.appBar',
    'component.tabBar',
    'component.sheet',
    'component.dialog',
    'component.toast',
    'component.emptyState',
    'component.loading',
    'component.carousel',
    'component.media',
    'component.text',
    'component.order',
    'screen.common',
    'screen.home',
    'screen.market',
    'screen.lounge',
    'screen.chat',
    'screen.me',
    'screen.broadcast',
    'screen.settings',
    'screen.search',
    'screen.profile',
    'screen.product',
    'screen.scheduledBroadcast',
    'nav.tabs',
    'nav.aliases',
    'text.ko',
    'text.en',
    'text.vi',
    'asset.icons',
    'asset.images',
    'page.index',
];
export const UI_CONFIG_APP_FILE_NAMES = UI_CONFIG_FILE_NAMES.filter((name) => UI_CONFIG_FILES[name].appDelivered !== false);
export const isUiConfigFileName = (value) => typeof value === 'string' && Object.prototype.hasOwnProperty.call(UI_CONFIG_FILES, value);
export function formatUiConfigReport(entries) {
    return UI_CONFIG_APP_FILE_NAMES.flatMap((name) => {
        const entry = entries[name];
        if (!entry)
            return [];
        const fingerprint = entry.fingerprint.slice(0, UI_CONFIG_REPORT_FINGERPRINT_LENGTH);
        return [`${name}:${fingerprint}${entry.builtIn ? '.b' : ''}`];
    }).join(',');
}
export function parseUiConfigReport(value) {
    const result = new Map();
    if (typeof value !== 'string' || value.length === 0)
        return result;
    for (const pair of value.slice(0, 4000).split(',')) {
        const [name, raw] = pair.split(':');
        if (!isUiConfigFileName(name) || !raw || UI_CONFIG_FILES[name].appDelivered === false)
            continue;
        const builtIn = raw.endsWith('.b');
        const fingerprint = builtIn ? raw.slice(0, -2) : raw;
        if (/^[\da-f]{1,64}$/.test(fingerprint))
            result.set(name, { fingerprint, builtIn });
    }
    return result;
}
export function mergeUiConfigPayload(name, builtIn, received) {
    return UI_CONFIG_FILES[name].mergeOverBuiltIn(builtIn, received);
}
//# sourceMappingURL=files.js.map